/**
 * Image Preload Optimizer
 * Balanced eager-loading for collection pages (mobile-first).
 */
(function() {
  'use strict';

  const gridSelector = '.product-grid';
  const carouselSelector = '.section-resource-list, .resource-list__carousel';
  const productGrid = document.querySelector(gridSelector);

  if (!productGrid && !document.querySelector(carouselSelector)) return;

  const isMobile = window.innerWidth < 750;
  const eagerGridCount = isMobile ? 8 : 16;
  const eagerCarouselCount = isMobile ? 3 : 5;
  const prefetchMargin = isMobile ? '300px' : '150px';
  const lowPriorityFoldMultiplier = 1.5;

  /** @param {HTMLImageElement | null} img */
  const eagerize = (img) => {
    if (!img) return;
    img.loading = 'eager';
    img.fetchPriority = 'high';
    img.decoding = 'async';
  };

  const observeLazy = (() => {
    if (!('IntersectionObserver' in window)) return null;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const img = entry.target;
          if (!(img instanceof HTMLImageElement)) return;

          if (img.dataset.srcset && !img.srcset) {
            img.srcset = img.dataset.srcset;
            img.removeAttribute('data-srcset');
          }
          if (img.dataset.src && !img.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }

          img.decoding = 'async';
          observer.unobserve(img);
        });
      },
      { rootMargin: prefetchMargin }
    );

    return (root = document) => {
      root.querySelectorAll('img[loading="lazy"]').forEach((img) => observer.observe(img));
    };
  })();

  function primeGridImages() {
    const items = document.querySelectorAll('.product-grid__item');
    items.forEach((item, index) => {
      if (index >= eagerGridCount) return;
      const img = item.querySelector('img[data-product-image], .gamified-card__image, .card-gallery img');
      eagerize(img);
    });
  }

  function setIntrinsicCardSize() {
    const firstCard = document.querySelector('.product-grid__item');
    if (!firstCard) return;
    const rect = firstCard.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    document.documentElement.style.setProperty('--product-card-intrinsic-width', `${Math.round(rect.width)}px`);
    document.documentElement.style.setProperty('--product-card-intrinsic-height', `${Math.round(rect.height)}px`);
  }

  function markLowPriorityItems() {
    const items = document.querySelectorAll('.product-grid__item');
    const fold = window.innerHeight * lowPriorityFoldMultiplier;

    items.forEach((item, index) => {
      if (index < eagerGridCount) return;
      const rect = item.getBoundingClientRect();
      if (rect.top > fold) {
        item.dataset.priority = 'low';
      }
    });
  }

  function primeCarouselImages() {
    const sections = document.querySelectorAll(carouselSelector);
    sections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      const isAboveFold = rect.top < window.innerHeight * 1.5;
      if (!isAboveFold) return;

      const imgs = section.querySelectorAll('img');
      imgs.forEach((img, index) => {
        if (index >= eagerCarouselCount) return;
        eagerize(img);
      });
    });
  }

  function runInitial() {
    setIntrinsicCardSize();
    primeGridImages();
    primeCarouselImages();
    if (observeLazy) observeLazy(document);
    markLowPriorityItems();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInitial, { once: true });
  } else {
    runInitial();
  }

  // Observe new product cards added by infinite scroll
  if (productGrid && 'MutationObserver' in window) {
    let scheduled = false;

    const observer = new MutationObserver((mutations) => {
      let hasNewNodes = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches?.('.product-grid__item') || node.querySelector?.('.product-grid__item')) {
            hasNewNodes = true;
          }
        });
      });

      if (!hasNewNodes || scheduled) return;

      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        if (observeLazy) observeLazy(productGrid);
        setIntrinsicCardSize();
        markLowPriorityItems();
      });
    });

    observer.observe(productGrid, { childList: true });
  }

  let resizeTimer;
  window.addEventListener(
    'resize',
    () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setIntrinsicCardSize();
        markLowPriorityItems();
      }, 150);
    },
    { passive: true }
  );
})();
